import { useEffect, useState } from "react";
import { Loader2, CheckSquare, X, Check, ShieldAlert, Bot } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useEntitlements } from "@/context/EntitlementContext";
import FeatureLocked from "@/components/FeatureLocked";
import { toast } from "sonner";

const STATE_STYLES = {
  pending_review: "bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
  executed: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
  rejected: "bg-rose-50 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300",
  failed: "bg-rose-50 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300",
};

export default function Approvals() {
  const { hasPermission } = useAuth();
  const { hasFeature, loading: entLoading } = useEntitlements();
  const canAct = hasPermission("approvals.act");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/approvals");
      setRows(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const act = async (id, action) => {
    setBusy(id + action);
    try {
      if (action === "reject") {
        const reason = window.prompt("Reason for rejection?") || "";
        await api.post(`/approvals/${id}/reject`, { reason });
        toast.success("Request rejected");
      } else {
        await api.post(`/approvals/${id}/approve`);
        toast.success("Approved & executed");
      }
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(null);
    }
  };

  if (!entLoading && !hasFeature("approval_workflows")) {
    return <div className="mx-auto max-w-xl py-10" data-testid="approvals-locked"><FeatureLocked featureKey="approval_workflows" /></div>;
  }
  if (loading) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-sky-600" /></div>;

  return (
    <div className="space-y-6" data-testid="approvals-page">
      <div>
        <p className="text-xs font-medium uppercase tracking-wider text-slate-400">Governance</p>
        <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 dark:text-ink-text">Approvals</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Maker-Checker queue for AI-assisted document changes. AI proposes — a human approves. Nobody approves their own high-risk request.</p>
      </div>

      {rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center text-sm text-slate-500 dark:border-ink-line" data-testid="approvals-empty">
          <CheckSquare className="mx-auto mb-3 h-8 w-8 text-slate-300" />
          No approval requests yet.
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.id} data-testid={`approval-${r.id}`} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-ink-line dark:bg-ink-card">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-slate-500 dark:bg-ink-panel"><Bot className="h-5 w-5" /></div>
                  <div>
                    <div className="font-display font-semibold text-slate-900 dark:text-ink-text">{r.tool_key}</div>
                    <div className="font-mono-tf text-[11px] text-slate-400">{r.target_entity_type} · action {r.action_id?.slice(0, 8)}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {r.risk_class === "high" && (
                    <span className="flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-semibold uppercase text-rose-600 dark:bg-rose-500/15 dark:text-rose-300">
                      <ShieldAlert className="h-3 w-3" /> high risk
                    </span>
                  )}
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATE_STYLES[r.state] || "bg-slate-100 text-slate-500"}`}>{r.state}</span>
                </div>
              </div>
              {r.rejection_reason && <p className="mt-2 text-xs text-rose-500">Reason: {r.rejection_reason}</p>}
              {canAct && r.state === "pending_review" && (
                <div className="mt-4 flex gap-2 border-t border-slate-100 pt-3 dark:border-ink-line">
                  <button onClick={() => act(r.id, "approve")} disabled={busy} data-testid={`approve-${r.id}`}
                    className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-60">
                    {busy === r.id + "approve" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Approve
                  </button>
                  <button onClick={() => act(r.id, "reject")} disabled={busy} data-testid={`reject-${r.id}`}
                    className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold text-rose-600 hover:bg-rose-50 dark:border-ink-line dark:hover:bg-rose-500/10">
                    <X className="h-4 w-4" /> Reject
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
